import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import { KEYBOARD_SHORTCUTS, KeyboardShortcutDefinition } from './keyboard-shortcuts.catalog';

/**
 * Escuta combinações de teclas globais e dispara o `run` do catálogo
 * (`keyboard-shortcuts.catalog.ts`) quando uma combinação corresponde.
 *
 * Segue o mesmo padrão de bootstrap do `DebugService`: injete esta classe
 * (mesmo sem usar a instância) em `AppComponent` para ativar o listener.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardShortcutsService implements OnDestroy {
  private static INITIALIZED = false;

  private readonly injector = inject(Injector);

  private readonly isMac = navigator.userAgent.toLowerCase().includes('mac');

  private readonly keydownHandler = (event: KeyboardEvent): void => {
    const shortcut = KEYBOARD_SHORTCUTS.find((candidate) => this.matches(event, candidate));
    if (!shortcut) return;

    event.preventDefault();
    shortcut.run(this.injector);
  };

  public constructor() {
    if (KeyboardShortcutsService.INITIALIZED) return;
    KeyboardShortcutsService.INITIALIZED = true;

    window.addEventListener('keydown', this.keydownHandler);
  }

  public ngOnDestroy(): void {
    window.removeEventListener('keydown', this.keydownHandler);
    KeyboardShortcutsService.INITIALIZED = false;
  }

  private matches(event: KeyboardEvent, shortcut: KeyboardShortcutDefinition): boolean {
    const combo = this.isMac ? shortcut.keys.mac : shortcut.keys.winLinux;
    const [modifiers, key] = this.parseCombo(combo);

    return (
      event.key?.toLowerCase() === key &&
      event.metaKey === modifiers.has('meta') &&
      event.ctrlKey === modifiers.has('ctrl') &&
      event.shiftKey === modifiers.has('shift') &&
      event.altKey === modifiers.has('alt')
    );
  }

  private parseCombo(combo: string): [Set<string>, string] {
    const parts = combo.toLowerCase().split('+');
    const key = parts.pop() ?? '';
    return [new Set(parts), key];
  }
}
